import {
  AppBrand,
  currentBrand,
  hasMegaPayBusinessCapabilities,
  isPosHost,
} from '../../../../_metronic/helpers/brand'
import {InstallEnvironment} from './installEnvironment'

/**
 * «معتمد آسا» for Android (repository ASATSPApp): the panel in an Android app with the phone's own
 * abilities - files saved into Download, Android's print and share sheet, the panel's notifications
 * without Google, a fingerprint lock. Its contract with the panel is ASATSPApp docs/bridge.md.
 */
export const ANDROID_APP = {
  /** The download page, with the steps of a first install. */
  page: 'https://download.asatsp.ir/App/',
  /** The newest direct build, at an address that never changes. */
  apk: 'https://download.asatsp.ir/App/ASATSP.apk',
  /**
   * The stores' pages, once the app is published there. Myket's agreement (§6.21) wants Myket
   * beside every other store the app is linked from: add both, or neither.
   */
  stores: [] as {name: string; url: string}[],
}

/** The app's notifications on this phone: the permission, the app's own switch, and both together. */
export type NativeNotifications = {
  permission: 'granted' | 'prompt' | 'denied'
  enabled: boolean
  active: boolean
}

/** What the Android app gives the page (asa-bridge.js), as window.asaApp. */
export type AsaNativeApp = {
  platform: 'android'
  version: string
  store: 'direct' | 'bazaar' | 'myket'
  /** false in the Bazaar and Myket builds (1.0.1+): see hidesPayments. */
  payments?: boolean
  saveFile: (blob: Blob, name: string) => Promise<boolean>
  openFile: (blob: Blob, name: string) => Promise<boolean>
  share: (data: ShareData) => Promise<void>
  print: () => void
  copyText: (text: string) => Promise<void>
  openSettings: () => void
  notifications: {
    state: () => Promise<NativeNotifications | null>
    enable: () => Promise<NativeNotifications | null>
    disable: () => Promise<NativeNotifications | null>
  }
}

/** The app's bridge, when the page runs inside ASA's Android app. */
export const nativeApp = (): AsaNativeApp | undefined => {
  if (typeof window === 'undefined') return undefined
  const app = (window as Window & {asaApp?: AsaNativeApp}).asaApp
  return app && app.platform === 'android' ? app : undefined
}

/**
 * Inside ASA's Android app: its bridge is there, or its user agent's mark («ASATSP/<version>») - the
 * mark is there from the first byte, even in an old WebView that runs the bridge only later.
 */
export const isInNativeApp = (userAgent?: string): boolean => {
  if (nativeApp()) return true
  const ua = userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent)
  return /\bASATSP\/\d/.test(ua || '')
}

/**
 * Bazaar and Myket allow no payment in an app (Bazaar's review, 1405/08/06: «پرداخت را بردارید»): in their builds the
 * panel offers no charge, subscription, purchase, price or online payment, and says nothing of paying elsewhere. The
 * app says so as asaApp.payments (1.0.1+); 1.0.0 only by its store, in asaApp.store and in its user agent's mark
 * «ASATSP/1.0.0 (android; bazaar)», which is there before the bridge. The direct build and the browser keep everything.
 */
export const hidesPayments = (userAgent?: string): boolean => {
  const app = nativeApp()
  if (app && typeof app.payments === 'boolean') return !app.payments
  if (app) return app.store !== 'direct'
  const ua = userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent)
  const store = /\bASATSP\/[\d.]+ \(android; ([a-z]+)\)/.exec(ua || '')?.[1]
  return store !== undefined && store !== 'direct'
}

/** In place of a fee, a charge or a payment in those builds: no price, and no other way to pay. */
export const STORE_BUILD_UNAVAILABLE_TEXT = 'این کار در این نسخهٔ برنامه در دسترس نیست.'

/**
 * The Android app is ASA's own panel's: not the bank's install, which is not this app, nor a POS
 * device, which has its own.
 */
export const androidAppAvailable = (
  brand: AppBrand = currentBrand,
  posHost: boolean = isPosHost
): boolean => hasMegaPayBusinessCapabilities(brand) && !posHost

/** Whether the install offers propose the Android app: on an Android phone or tablet, where it is. */
export const offersAndroidApp = (
  env: InstallEnvironment,
  brand: AppBrand = currentBrand,
  posHost: boolean = isPosHost
): boolean => env.os === 'android' && androidAppAvailable(brand, posHost)
